import Vue from "vue";
import Vuex, { StoreOptions } from "vuex";
import { espoedModule } from "./espoed/index";
import {
  AuthenticationConfiguration,
  ApplicationConfiguration,
  UserPermissions,
  RootState,
} from "@/types/store";
import { AZNCONNECT_API } from "@/services/aznconnect-api";

Vue.use(Vuex);

const store: StoreOptions<RootState> = {
  state: {
    authenticationConfiguration: {
      authenticationType: "",
      clientId: "",
      authority: "",
      redirectUri: "",
      postLogoutRedirectUri: "",
      scopes: [],
    },
    applicationConfiguration: {
      version: process.env.VERSION || "",
      showMainMenu: true,
      autoHideNonClosedReferrals: false,
      manualCloseEnabled: false,
      patientNumberEditEnabled: false,
      inboxNotificationsRepeatUntilReadAll: false,
      professionalSummaryEnabled: false,
      selfReferrerEnabled: false,
      patientSearchByPatientNrEnabled: true,
      patientSearchByPatientBsnEnabled: true,
      patientSearchByFamilyNameEnabled: true,
      patientSearchByBirthDateEnabled: true,
      patientNumberDepartmentOverruleEnabled: false,
      zorgIDEnvironmentCode: "",
      enableTeamsCallAmbulance: false,
    },
    socketIsConnected: false,
    userPermissions: {
      view: false,
      assignPatientNumber: false,
      requestProfessionalSummary: false,
      setReferralState: false,
      copyLinkToClipboard: false,
      downloadReferralAsPdf: false,
      toggleAdvancedMode: false,
      toggleDeveloperMode: false,
      patientTransporterView: false,
    },
    playSoundNotificationReferrals: false,
  },
  getters: {
    authenticationConfiguration(state): AuthenticationConfiguration {
      return state.authenticationConfiguration;
    },
    applicationConfiguration(state): ApplicationConfiguration {
      return state.applicationConfiguration;
    },
    version(state): string {
      return state.applicationConfiguration.version;
    },
    showMainMenu(state): boolean {
      return state.applicationConfiguration.showMainMenu;
    },
    socketIsConnected(state): boolean {
      return state.socketIsConnected;
    },
    userPermissions(state): UserPermissions {
      return state.userPermissions;
    },
    playSoundNotificationReferrals(state): boolean {
      return state.playSoundNotificationReferrals;
    },
  },
  mutations: {
    setApplicationConfiguration(state, payload) {
      const toBool = (value: string) => value === "true";

      state.applicationConfiguration.autoHideNonClosedReferrals = toBool(
        payload.AutoHideNonClosedReferrals,
      );
      state.applicationConfiguration.manualCloseEnabled = toBool(
        payload.EnableManualClose,
      );
      state.applicationConfiguration.patientNumberEditEnabled = toBool(
        payload.PatientNumberEditEnabled,
      );
      state.applicationConfiguration.inboxNotificationsRepeatUntilReadAll = toBool(
        payload.InboxNotificationsRepeatUntilReadAll,
      );
      state.applicationConfiguration.professionalSummaryEnabled = toBool(
        payload.ProfessionalSummaryEnabled,
      );
      state.applicationConfiguration.selfReferrerEnabled = toBool(
        payload.SelfReferrerEnabled,
      );
      state.applicationConfiguration.patientSearchByPatientNrEnabled = toBool(
        payload.PatientSearchByPatientNrEnabled,
      );
      state.applicationConfiguration.patientSearchByPatientBsnEnabled = toBool(
        payload.PatientSearchByPatientBsnEnabled,
      );
      state.applicationConfiguration.patientSearchByFamilyNameEnabled = toBool(
        payload.PatientSearchByFamilyNameEnabled,
      );
      state.applicationConfiguration.patientSearchByBirthDateEnabled = toBool(
        payload.PatientSearchByBirthDateEnabled,
      );
      state.applicationConfiguration.patientNumberDepartmentOverruleEnabled = toBool(
        payload.PatientNumberDepartmentOverruleEnabled,
      );
      state.applicationConfiguration.zorgIDEnvironmentCode =
        payload.ZorgIDEnvironmentCode;
      state.applicationConfiguration.enableTeamsCallAmbulance = toBool(
        payload.EnableTeamsCallAmbulance,
      );
    },
    setUserPermissions(state, payload) {
      state.userPermissions.view = payload.View;
      state.userPermissions.assignPatientNumber = payload.AssignPatientNumber;
      state.userPermissions.requestProfessionalSummary =
        payload.RequestProfessionalSummary;
      state.userPermissions.setReferralState = payload.SetReferralState;

      state.userPermissions.copyLinkToClipboard = payload.CopyLinkToClipboard;
      state.userPermissions.downloadReferralAsPdf =
        payload.DownloadReferralAsPdf;
      state.userPermissions.toggleAdvancedMode = payload.ToggleAdvancedMode;
      state.userPermissions.toggleDeveloperMode = payload.ToggleDeveloperMode;
      state.userPermissions.patientTransporterView = payload.PatientTransporterView;
    },
  },
  actions: {
    loadAuthenticationConfiguration({ state }) {
      return AZNCONNECT_API.get("/Settings/AuthenticationConfiguration").then(
        function(response) {
          state.authenticationConfiguration.authenticationType =
            response.data.AuthenticationType == 0
              ? "unknown"
              : response.data.AuthenticationType == 1
              ? "windows"
              : response.data.AuthenticationType == 2
              ? "msal"
              : "unknown";
          state.authenticationConfiguration.clientId = response.data.ClientId;
          state.authenticationConfiguration.authority = response.data.Authority;
          state.authenticationConfiguration.redirectUri =
            response.data.RedirectUri;
          state.authenticationConfiguration.postLogoutRedirectUri =
            response.data.PostLogoutRedirectUri;
          state.authenticationConfiguration.scopes = response.data.Scopes;
        },
      );
    },
    async loadConfiguration({ commit }) {
      try {
        const response = await AZNCONNECT_API.get("/Settings/AppConfiguration");

        commit("setApplicationConfiguration", response.data);
      } catch (error) {
        console.error("Failed to load application configuration:", error);

        throw error;
      }
    },
    loadPermissions({ commit, state }) {
      return AZNCONNECT_API.get("/Settings/UserPermissions").then(function(
        response,
      ) {
        commit("setUserPermissions", response.data);
      });
    },
    enableMainMenu({ state }, payload: boolean) {
      state.applicationConfiguration.showMainMenu = payload;
    },
    socketConnected({ state }) {
      state.socketIsConnected = true;
    },
    socketDisconnected({ state }) {
      state.socketIsConnected = false;
    },
    toggleNewReferralSound({ state }, payload: boolean) {
      state.playSoundNotificationReferrals = payload;
    },
  },
  modules: {
    espoed: espoedModule,
  },
};

export default new Vuex.Store<RootState>(store);
